import React from "react";
import table from "./translationTable";
import { getGlobalLang } from "./langSource";

const SKIP_PROPS = ['value', 'id', 'name', 'key']

function isExactOrSpaceBounded(text, key) {
  if (text === key) return true;

  // key 뒤에 공백만 허용
  if (text.startsWith(key)) {
    const rest = text.slice(key.length);
    return /^[\s]*$/.test(rest);
  }

  return false;
}

/* =========================================================
 * 내부용: lang 을 인자로 받는 텍스트 번역
 * =======================================================*/
function translateTextInternal(text, lang) {
  // 한국어 모드에서는 번역하지 않음
  if (lang === "ko") return text;
  if (text == null) return text;

  // 문자열 변환 (방어적 처리)
  const str = typeof text === "string" ? text : String(text);

  // 🔥 앞/뒤 공백 분리 보존
  const match = str.match(/^(\s*)([\s\S]*?)(\s*)$/);
  if (!match) return str;

  const [, leading, core, trailing] = match;

  // 공백만 있는 경우 그대로 반환
  if (!core) return str;

  // ===============================
  // 1) 완전 일치 매핑
  // ===============================
  let translated = table[core];

  if (typeof translated === "string") {
    if (translated.includes("\n")) {
      const parts = translated.split("\n");
      return parts.map((p, idx) =>
        idx === 0
          ? `${leading}${p}`
          : [<br key={idx} />, `${p}${trailing}`]
      );
    }

    return `${leading}${translated}${trailing}`;
  }

  // ===============================
  // 2) 부분 문자열 치환 (공백만 허용)
  // ===============================
  for (const key of Object.keys(table).sort((a, b) => b.length - a.length)) {
    if (isExactOrSpaceBounded(core, key)) {
      const replaced = core.replace(key, table[key]);

      if (replaced.includes("\n")) {
        const parts = replaced.split("\n");
        return parts.flatMap((p, idx) =>
          idx === 0
            ? [`${leading}${p}`]
            : [<br key={`br-${idx}`} />, `${p}${trailing}`]
        );
      }

      return `${leading}${replaced}${trailing}`;
    }
  }

  // ===============================
  // 3) 아무 매핑도 없으면 원문 반환
  // ===============================
  return str;
}

/* =========================================================
 * 외부에서 직접 호출하는 API (lang 자동 결정)
 *  - 헤더, 버튼 label 등에 직접 쓰는 용도
 * =======================================================*/
export function translateText(text) {
  const lang = getGlobalLang();
  return translateTextInternal(text, lang);
}

/* =========================================================
 * 문자열 children / props 자동 번역 (lang 주입 버전)
 * =======================================================*/
function translateChild(child) {
  // 문자열만 번역
  if (typeof child === "string") {
    return translateText(child);
  }

  if (React.isValidElement(child)) {
    const { children } = child.props ?? {};

    if (child.props?.value != null) {
      if (children == null) return child;

      // children이 문자열
      if (typeof children === "string") {
        return React.cloneElement(child, null, translateText(children));
      }

      // children이 배열
      if (Array.isArray(children)) {
        const nextChildren = React.Children.map(children, (c) =>
          typeof c === "string" ? translateText(c) : c
        );

        return React.cloneElement(child, null, nextChildren);
      }

      return child;
    }

    if (children == null) return child;

    // children이 문자열
    if (typeof children === "string") {
      const translated = translateText(children);

      // 번역 결과가 배열이면 그대로 children으로
      if (Array.isArray(translated)) {
        return React.cloneElement(child, null, translated);
      }

      return React.cloneElement(child, null, translated);
    }

    // children이 배열
    if (Array.isArray(children)) {
      let changed = false;

      const nextChildren = React.Children.map(children, (c) => {
        // 문자열
        if (typeof c === "string") {
          const t = translateText(c);
          if (t !== c) changed = true;
          return t;
        }

        // Tab label 번역
        if (
          React.isValidElement(c) &&
          typeof c.props?.label === "string"
        ) {
          const t = translateText(c.props.label);
          if (t !== c.props.label) {
            changed = true;
            return React.cloneElement(c, {
              ...c.props,
              label: t,
            });
          }
        }

        return c;
      });

      if (!changed) return child;

      return React.cloneElement(child, null, nextChildren);
    }

    return child;
  }

  return child;
}

function translateProps(props = {}, lang) {
  if (!props) return props;

  const newProps = {};

  for (const key in props) {
    if (!Object.prototype.hasOwnProperty.call(props, key)) continue;
    const val = props[key];

    // 절대 번역하면 안 되는 props
    if (SKIP_PROPS.includes(key)) {
      newProps[key] = val;
      continue;
    }

    // React 내부 관리 속성
    if (key === "ref" || key === "_owner" || key === "_store") {
      newProps[key] = val;
      continue;
    }

    // label 전용 번역
    if (key === "label" && typeof val === "string") {
      newProps[key] = translateText(val);
      continue;
    }

    // 문자열만 번역
    if (typeof val === "string") {
      newProps[key] = translateTextInternal(val, lang);
      continue;
    }

    // 배열이면 내부 문자열만 번역
    if (Array.isArray(val)) {
      newProps[key] = val.map((v) =>
        typeof v === "string" ? translateTextInternal(v, lang) : v
      );
      continue;
    }

    newProps[key] = val;
  }

  return newProps;
}

/* =========================================================
 * createElement 훅킹 — React 렌더 이전에 모든 문자열 번역
 * =======================================================*/

const originalCreateElement = React.createElement;

React.createElement = (type, props, ...children) => {
  // 실행 시점 기준 현재 URL/스토리지에서 언어 한 번만 계산
  const lang = getGlobalLang();

  // Provider/Router/ThemeProvider 계열은 스킵
  const typeName =
    typeof type === "function"
      ? type.name
      : typeof type === "string"
      ? type
      : String(type);

  const skip =
    typeName.endsWith("Provider") ||
    typeName.includes("Router") ||
    typeName.includes("ThemeProvider");

  const newProps = skip ? props : translateProps(props, lang);

  const newChildren = skip
    ? children
    : children.map((c) => translateChild(c, lang));

  return originalCreateElement(type, newProps, ...newChildren);
};

// 이 모듈은 "불러오기만 하면 전역 훅킹"이 목적이기 때문에
// 사용할 값은 없고, default 는 의미 없는 값으로 export
export default null;
